import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { priorityColor } from '../../shared/constants';
import DepartmentEmployeeFields from '../../tasks/components/DepartmentEmployeeFields';
import TaskFieldsBlock, { emptyTaskFields, hoursOrNull, TaskFieldsValue, taskFieldsProblem } from '../../tasks/components/TaskFieldsBlock';
import { useCapacityConfirm, wasCancelled } from '../../tasks/components/useCapacityConfirm';
import { useTaskMeta } from '../../tasks/components/useTaskMeta';
import { tasksApi } from '../../tasks/tasks.api';
import type { Option as PriorityOption } from '../../tasks/tasks.types';
import { Card, SectionTitle } from '../../shared/components';
import { Table } from '../../shared/components/data-display';
import useAsyncData from '../../shared/hooks/useAsyncData';
import AssignedWorkCalendar from '../components/AssignedWorkCalendar';
import {
  AnalystJob,
  assignWork,
  getAnalystJobs,
  getJobTasks,
} from '../services/assignment.api';

/** What is being assigned: tasks, or a site visit (which the legacy endpoint still takes). */
type AssignKind = 'tasks' | 'siteVisit';
const SHIFTS = [
  { label: 'First Half', value: 'first_half' },
  { label: 'Second Half', value: 'second_half' },
  { label: 'Full Day', value: 'full_day' },
];

/**
 * A task row: the same fields, in the same layout, as the NEW TASK form on the Tasks
 * screen — both render {@link TaskFieldsBlock} — because both create the same thing: a
 * task in the task & workload module, planned day by day over its dates.
 */
type TaskRow = TaskFieldsValue;
const emptyTask = emptyTaskFields;

/** Shown until `/api/tasks/meta` answers, so the picker is never empty. */
const FALLBACK_PRIORITIES: PriorityOption[] = ['URGENT', 'HIGH', 'MEDIUM', 'LOW'].map((code, i) => ({
  id: -(i + 1),
  code,
  label: code.charAt(0) + code.slice(1).toLowerCase(),
  color: null,
}));

/** Every picker on this screen is a type-to-filter list, so a long job or employee list
 *  is searched rather than scrolled. */
interface Option {
  id: string;
  label: string;
}

const SHIFT_OPTIONS: Option[] = SHIFTS.map((shift) => ({ id: shift.value, label: shift.label }));

interface AssignWorkViewProps {
  /** Preselects the job — used when the screen is opened from a job's own row. */
  initialJobId?: number | string;
  /** Hides the screen heading, for when the caller already has one (the dialog). */
  hideTitle?: boolean;
  /** Called after work is assigned, so a caller can refresh what it shows. */
  onAssigned?: () => void;
}

/**
 * Assign drawing/design tasks or a site visit to an employee against a job.
 * Tasks are created through `POST /api/tasks` — the same fields and rules as the Tasks
 * screen's NEW TASK form, so they get history, a daily plan and workload tracking. Site
 * visits still go through `POST /api/analyst/job/{jobId}/assign`.
 */
export function AssignWorkView({ initialJobId, hideTitle = false, onAssigned }: AssignWorkViewProps = {}) {
  /**
   * Opened from a job's own row the job is already decided, so the picker — and the list
   * that feeds it — are not needed. The standalone `/work/assign` screen passes no job and
   * keeps both.
   */
  const jobLocked = initialJobId !== undefined && initialJobId !== null && initialJobId !== '';

  const fetchJobs = useCallback(() => getAnalystJobs(), []);

  const { data: jobs, loading: jobsLoading, error: jobsError, refetch: refetchJobs } =
    useAsyncData<AnalystJob[]>(fetchJobs, [], {
      enabled: !jobLocked,
      code: 'ASSIGN_JOBS_FAILED',
      errorMessage: 'Failed to load jobs',
    });


  const [jobId, setJobId] = useState<string>(initialJobId ? String(initialJobId) : '');
  const [kind, setKind] = useState<AssignKind>('tasks');
  /** Narrows the employee list; null means every department. */
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [requireAcceptance, setRequireAcceptance] = useState(true);
  const [tasks, setTasks] = useState<TaskRow[]>([emptyTask()]);
  const meta = useTaskMeta();
  const priorities = meta?.priorities.length ? meta.priorities : FALLBACK_PRIORITIES;
  const { run: runWithCapacityConfirm, dialog: capacityDialog } = useCapacityConfirm();

  const [assignedDate, setAssignedDate] = useState('');
  const [assignedShift, setAssignedShift] = useState(SHIFTS[0].value);
  const [isFreeVisit, setIsFreeVisit] = useState(true);
  const [visitCount, setVisitCount] = useState('1');
  const [directorVisit, setDirectorVisit] = useState(false);
  const [analystVisit, setAnalystVisit] = useState(false);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const isSiteVisit = kind === 'siteVisit';

  // Existing assignments on the selected job, so you can see the current load.
  const fetchTasks = useCallback(() => (jobId ? getJobTasks(jobId) : Promise.resolve([])), [jobId]);
  const { data: existingTasks, loading: tasksLoading, refetch: refetchTasks } = useAsyncData<any[]>(
    fetchTasks,
    [],
    {
      enabled: Boolean(jobId),
      code: 'JOB_TASKS_FAILED',
      errorMessage: 'Failed to load existing assignments',
      toastOnError: false,
    }
  );

  const jobOptions: Option[] = useMemo(
    () =>
      jobs.map((job) => ({
        id: String(job.id),
        label: `${job.job_code ? `${job.job_code} — ` : ''}${job.job_name ?? `Job #${job.id}`}`,
      })),
    [jobs]
  );

  // Drawings belong to a project: picking another job clears the ones already chosen.
  useEffect(() => {
    setTasks((prev) =>
      prev.some((t) => t.work.structuralDrawings.length)
        ? prev.map((t) => ({ ...t, work: { ...t.work, structuralDrawings: [] } }))
        : prev
    );
  }, [jobId]);

  /**
   * A preselected job that the assignable-jobs list does not carry would sit in state
   * invisibly — the picker shows nothing while the Assign button stays enabled. Drop it
   * so the user is asked to choose.
   */
  useEffect(() => {
    if (jobLocked || !jobId || jobOptions.length === 0) return;
    if (!jobOptions.some((option) => option.id === jobId)) setJobId('');
  }, [jobLocked, jobId, jobOptions]);

  const updateTask = (index: number, next: TaskRow) =>
    setTasks((prev) => prev.map((task, i) => (i === index ? next : task)));

  const resetForm = () => {
    setTasks([emptyTask()]);
    setEmployeeId(null);
    setAssignedDate('');
  };

  const handleAssign = async () => {
    if (!jobId) {
      setFormError('Select a job first.');
      return;
    }

    setFormError(null);
    setSaving(true);

    try {
      if (isSiteVisit) {
        if (!employeeId || !assignedDate) {
          setFormError('Site engineer and visit date are required.');
          setSaving(false);
          return;
        }

        await assignWork(jobId, {
          department: 'Site Visit',
          site_engineer: employeeId,
          assigned_date: assignedDate,
          assigned_shift: assignedShift,
          is_free_visit: isFreeVisit,
          visit_count: Number(visitCount) || 1,
          director_visit: directorVisit,
          analyst_visit: analystVisit,
        });
      } else {
        const validTasks = tasks.filter((task) => task.title.trim());
        if (!employeeId || validTasks.length === 0) {
          setFormError('Employee and at least one task title are required.');
          setSaving(false);
          return;
        }
        const invalid = validTasks.map((t) => [t, taskFieldsProblem(t)] as const).find(([, problem]) => problem);
        if (invalid) {
          setFormError(`${invalid[1]} Check "${invalid[0].title.trim()}".`);
          setSaving(false);
          return;
        }

        // One task per row, through the task module; a row that would put the employee over
        // capacity asks for confirmation first. Rows already created stay created if a
        // later one fails, and the message says which ones those were.
        const created: string[] = [];
        for (const t of validTasks) {
          try {
            const detail = await runWithCapacityConfirm((allowOverCapacity) =>
              tasksApi.create({
                title: t.title.trim(),
                description: t.description.trim() || undefined,
                projectId: Number(jobId),
                workActivityId: t.work.workActivityId,
                typeOfWorkActivityId: t.work.typeOfWorkActivityId,
                structuralWorkType: t.work.structuralWorkType,
                structuralDrawings: t.work.structuralDrawings,
                priority: t.priority,
                startDate: t.startDate || null,
                dueDate: t.dueDate || null,
                estimatedHours: hoursOrNull(t.estimatedHours),
                allocatedHours: hoursOrNull(t.allocatedHours),
                assigneeId: employeeId,
                requireAcceptance,
                allowOverCapacity,
              })
            );
            created.push(detail.task.taskCode);
          } catch (err) {
            if (created.length) toast.success(`Created ${created.join(', ')}`);
            if (created.length) await refetchTasks();
            if (!wasCancelled(err)) throw err;
            setFormError(`Stopped at "${t.title.trim()}"${created.length ? ` — ${created.join(', ')} already created` : ''}.`);
            setSaving(false);
            return;
          }
        }
        toast.success(`Created ${created.join(', ')}`);
        resetForm();
        await refetchTasks();
        onAssigned?.();
        return;
      }

      toast.success('Work assigned');
      resetForm();
      await refetchTasks();
      onAssigned?.();
    } catch (err) {
      setFormError(
        logError({ message: 'Failed to assign work', error: err, code: 'ASSIGN_WORK_FAILED' })
      );
    } finally {
      setSaving(false);
    }
  };

  /**
   * Keyed to what `GET /api/analyst/job/{id}/tasks` actually returns — `task_name`,
   * `allotted_time` and a nested `assigned_to`. The employee has to be read through a
   * `renderCell` because the table reads flat keys only.
   */
  const taskColumns = [
    { id: 'id', dataKey: 'id', label: 'ID', align: 'center' as const, sortable: true },
    { id: 'task_name', dataKey: 'task_name', label: 'Task', align: 'left' as const, sortable: true },
    {
      id: 'assigned_to',
      dataKey: 'assigned_to',
      label: 'Assigned To',
      align: 'left' as const,
      renderCell: (value: any) => value?.employee_name ?? 'Unassigned',
    },
    { id: 'allotted_time', dataKey: 'allotted_time', label: 'Allotted', align: 'center' as const },
    {
      id: 'priority',
      dataKey: 'priority',
      label: 'Priority',
      align: 'center' as const,
      renderCell: (value: any) => <PriorityText priority={value} />,
    },
    { id: 'status', dataKey: 'status', label: 'Status', align: 'center' as const },
  ];

  return (
    <Stack gap={3}>
      {!hideTitle && <SectionTitle title='ASSIGN WORK' />}

      {!jobLocked && jobsError && (
        <Alert severity='error' action={<Button onClick={refetchJobs}>Retry</Button>}>
          {jobsError}
        </Alert>
      )}
      <ToggleButtonGroup
        exclusive
        size='small'
        value={kind}
        onChange={(_e, v: AssignKind | null) => v && setKind(v)}
        sx={{ alignSelf: 'flex-start' }}
      >
        <ToggleButton value='tasks'>Tasks</ToggleButton>
        <ToggleButton value='siteVisit'>Site Visit</ToggleButton>
      </ToggleButtonGroup>

      <Card>
        <Stack gap={2.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
            {!jobLocked && (
              <Autocomplete
                options={jobOptions}
                value={jobOptions.find((option) => option.id === jobId) ?? null}
                onChange={(_event, option) => setJobId(option ? option.id : '')}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={jobsLoading}
                openOnFocus
                autoHighlight
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Job'
                    required
                    placeholder={jobsLoading ? 'Loading jobs…' : 'Type to search jobs'}
                  />
                )}
              />
            )}

            <DepartmentEmployeeFields
              departments={meta?.departments ?? []}
              employees={meta?.employees ?? []}
              departmentId={departmentId}
              onDepartmentChange={setDepartmentId}
              employeeId={employeeId}
              onEmployeeChange={setEmployeeId}
              employeeLabel={isSiteVisit ? 'Site Engineer' : 'Employee'}
              employeeRequired
              loading={!meta}
            />
          </Stack>

          {isSiteVisit ? (
            <Stack gap={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                <TextField
                  label='Visit Date'
                  type='date'
                  value={assignedDate}
                  onChange={(e) => setAssignedDate(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                  required
                />
                <Autocomplete
                  options={SHIFT_OPTIONS}
                  value={SHIFT_OPTIONS.find((option) => option.id === assignedShift) ?? SHIFT_OPTIONS[0]}
                  onChange={(_event, option) => option && setAssignedShift(option.id)}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disableClearable
                  openOnFocus
                  autoHighlight
                  fullWidth
                  renderInput={(params) => <TextField {...params} label='Shift' />}
                />
                <TextField
                  label='Visit Count'
                  type='number'
                  value={visitCount}
                  onChange={(e) => setVisitCount(e.target.value)}
                  fullWidth
                />
              </Stack>

              <Stack direction='row' gap={2} flexWrap='wrap'>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isFreeVisit}
                      onChange={(e) => setIsFreeVisit(e.target.checked)}
                    />
                  }
                  label='Free visit (counts against the quoted allowance)'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={directorVisit}
                      onChange={(e) => setDirectorVisit(e.target.checked)}
                    />
                  }
                  label='Director visit'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={analystVisit}
                      onChange={(e) => setAnalystVisit(e.target.checked)}
                    />
                  }
                  label='Analyst visit'
                />
              </Stack>
            </Stack>
          ) : (
            <Stack gap={2}>
              <FormControlLabel
                control={<Switch checked={requireAcceptance} onChange={(e) => setRequireAcceptance(e.target.checked)} />}
                label='Employee must accept'
                sx={{ alignSelf: 'flex-start' }}
              />

              <Typography variant='subtitle2'>Tasks</Typography>
              {tasks.map((task, index) => (
                <Stack
                  key={index}
                  gap={2}
                  sx={{ p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}
                >
                  <TaskFieldsBlock
                    value={task}
                    onChange={(next) => updateTask(index, next)}
                    priorities={priorities}
                    activities={meta?.workActivities ?? []}
                    projectId={jobId ? Number(jobId) : null}
                    action={
                      <IconButton
                        color='error'
                        onClick={() => setTasks((prev) => prev.filter((_, i) => i !== index))}
                        disabled={tasks.length === 1}
                        aria-label='Remove task'
                      >
                        <Icon icon='mdi:trash-can-outline' width={20} />
                      </IconButton>
                    }
                  />
                </Stack>
              ))}

              <Button
                variant='text'
                startIcon={<Icon icon='mdi:plus' />}
                onClick={() => setTasks((prev) => [...prev, emptyTask()])}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add task
              </Button>
            </Stack>
          )}

          {formError && <Alert severity='error'>{formError}</Alert>}

          <Button
            variant='contained'
            onClick={handleAssign}
            disabled={saving || !jobId}
            sx={{ alignSelf: 'flex-end' }}
          >
            {saving ? 'Assigning…' : isSiteVisit ? 'Assign Site Visit' : 'Assign Tasks'}
          </Button>
        </Stack>
      </Card>

      <SectionTitle title='CURRENT ASSIGNMENTS' variant='subtitle' />
      <Table
        columns={taskColumns}
        data={existingTasks}
        loading={tasksLoading}
        emptyMessage={jobId ? 'No assignments on this job yet' : 'Select a job to see its assignments'}
      />
      {capacityDialog}
    </Stack>
  );
}

/**
 * The standalone screen: the assign form, and below it the week's assigned work.
 *
 * The calendar is only on this screen, not in {@link AssignWorkView} itself — the view is
 * also rendered inside the job dialog, where a whole week across every job is not what is
 * being looked at.
 */
export default function AssignWorkPage() {
  return (
    <div className='column gap-4'>
      <AssignWorkView />
      <AssignedWorkCalendar />
    </div>
  );
}

/**
 * A priority in its own colour - red High, yellow Medium, green Low, from the one
 * `PRIORITY_COLORS` table the task screens read, so the levels look the same everywhere.
 * An unrecognised value (or none) falls back to the ordinary text colour rather than
 * showing an uncoloured blank.
 */
function PriorityText({ priority }: { priority?: string | null }) {
  const color = priorityColor(priority);
  return (
    <Typography
      component='span'
      variant='body2'
      sx={{ color: color ?? 'text.primary', fontWeight: color ? 600 : 400 }}
    >
      {priority || '—'}
    </Typography>
  );
}
